/**
 * 送れていない録音を、止まったところの続きから送る（PRODUCT_DEV-1270）
 *
 * 1. 記録を作って音声の送り先をもらう（送り直しで記録が作ってあれば、その記録の送り先を出し直してもらう。
 *    もう音声を待っていなければ 409 なので、新しく作り直す）
 * 2. 音声を置く
 * 3. 処理を頼む（ここで 400 / 404 / 409 なら、サーバがもう進めている＝済んだとみなす。
 *    自由記録は音声が置いてあれば30分後にサーバが代わりに頼む・PRODUCT_DEV-772）
 *
 * 段が進むたびに `save` で端末へ書くので、途中で切れても次は続きからになる。
 * 通信は `deps` で差し込む（テストのため）。
 */
import type { PendingRecording, SendOutcome } from "./types";

export interface SendEndpoints {
  /**
   * 1回で送る種類（対話モード）。音声を置かず、request に送信番号（clientRecordId = 端末の id）を付けて
   * POST し、記録の ID を受け取る。同じ番号の送り直しには、サーバが前の記録を返す
   */
  single?: { url: string; idOf: (res: unknown) => string | undefined };
  /**
   * 送り先をもらって音声を置くだけの種類（議事録の区切り）。記録はもうあるので作らない。
   * 届けばサーバが文字起こしして議事録に入れる（確定のあとなら作り直す）
   */
  chunk?: { url: (item: PendingRecording) => string; body: (item: PendingRecording) => Record<string, unknown> };
  /** 中身を1回 POST するだけの種類（議事録の確定）。記録はもうあるので、404 は送り先が無い */
  post?: { url: (item: PendingRecording) => string; body: (item: PendingRecording) => Record<string, unknown> };
  /** 記録を作る（または送り先を出し直す）API。返す ID のキー名は種類ごとに違う */
  uploadUrl: string;
  /** 送り直しで前の記録を指す項目名（自由記録 meetingId / 音声メモ memoId） */
  serverIdField: "meetingId" | "memoId";
  /** 処理を頼む API の URL と中身 */
  submit: (serverId: string, item: PendingRecording) => { url: string; body: Record<string, unknown> };
}

export interface SendDeps {
  apiPost: <T>(url: string, body: unknown) => Promise<T>;
  putAudio: (url: string, item: PendingRecording) => Promise<Response>;
  save: (item: PendingRecording) => Promise<unknown>;
}

const statusOf = (err: unknown): number | undefined =>
  typeof (err as { status?: unknown })?.status === "number"
    ? (err as { status: number }).status
    : undefined;

/** 通信の問題（あとで送れば通るかもしれない）か。ステータスの無い失敗は通信が切れたもの */
export const isRetryable = (err: unknown): boolean => {
  const s = statusOf(err);
  return s === undefined || s >= 500 || s === 401 || s === 408 || s === 429;
};

/** 409 が続いたら人に任せるまでの回数（1分ごとの送り直しで約20分） */
const MAX_CONFLICT_ATTEMPTS = 20;

export async function sendPending(
  item: PendingRecording,
  ep: SendEndpoints,
  deps: SendDeps,
): Promise<SendOutcome> {
  let cur: PendingRecording = {
    ...item,
    attempts: item.attempts + 1,
    lastTriedAt: new Date().toISOString(),
  };
  const advance = async (patch: Partial<PendingRecording>) => {
    cur = { ...cur, ...patch };
    await deps.save(cur);
  };

  try {
    if (ep.single) {
      let res: unknown;
      try {
        // 端末の番号を送信番号として付ける。サーバは同じ番号なら作らずに前の記録を返す
        // （送れたのに返事が届かなかったときの送り直しで、記録を二重に作らない）
        // tenantId は認証の関門が本文から読む（無いと 400。サーバは JWT と突き合わせた値を使う）
        res = await deps.apiPost(ep.single.url, {
          ...cur.request,
          tenantId: cur.tenantId,
          clientRecordId: cur.id,
        });
      } catch (err) {
        const s = statusOf(err);
        // 同じ番号の送信をサーバが作っている途中。少しあとで送れば前の記録が返る。
        // 何度も続くなら（前の記録が消された など）人に任せる
        if (s === 409) return cur.attempts >= MAX_CONFLICT_ATTEMPTS ? { kind: "failed" } : { kind: "retry" };
        // 5xx は記録を作ったあとに落ちたのかもしれない。番号で二重にはならないが、
        // 続けて落ちる不具合なら1分ごとに叩き続けるので人に任せる
        if (s !== undefined && s >= 500) return { kind: "failed" };
        throw err;
      }
      const id = ep.single.idOf(res);
      return id ? { kind: "done", serverId: id } : { kind: "failed" };
    }
    if (ep.post) {
      try {
        await deps.apiPost(ep.post.url(cur), ep.post.body(cur));
      } catch (err) {
        if (statusOf(err) === 404) return { kind: "failed" };
        throw err;
      }
      return { kind: "done", serverId: String(cur.request.minutesId ?? "") };
    }
    if (ep.chunk) {
      let res: { uploadUrl?: string };
      try {
        res = await deps.apiPost(ep.chunk.url(cur), ep.chunk.body(cur));
      } catch (err) {
        // 議事録がもう無い（捨てた・ゴミ箱）なら、送り先が無い
        if (statusOf(err) === 404) return { kind: "failed" };
        throw err;
      }
      if (typeof res?.uploadUrl !== "string") return { kind: "failed" };
      const put = await deps.putAudio(res.uploadUrl, cur);
      if (!put.ok) {
        return put.status === 403 || put.status >= 500 ? { kind: "retry" } : { kind: "failed" };
      }
      return { kind: "done", serverId: String(cur.request.minutesId ?? "") };
    }
    if (cur.stage !== "uploaded") {
      let res: { uploadUrl: string } & Record<string, unknown>;
      try {
        res = await deps.apiPost(ep.uploadUrl, {
          ...cur.request,
          ...(cur.serverId ? { [ep.serverIdField]: cur.serverId } : {}),
        });
      } catch (err) {
        // 前に作った記録がもう音声を待っていない（30分たって片付けられた など）→ 新しく作る
        if (cur.serverId && statusOf(err) === 409) {
          await advance({ stage: "new", serverId: undefined });
          res = await deps.apiPost(ep.uploadUrl, cur.request);
        } else {
          throw err;
        }
      }
      const serverId = res[ep.serverIdField];
      if (typeof serverId !== "string") return { kind: "failed" };
      await advance({ stage: "created", serverId });

      const put = await deps.putAudio(res.uploadUrl, cur);
      if (!put.ok) {
        // 送り先の期限切れ（60秒）は 403 で来る。出し直してもらえば通るので、あとで送る。
        // 5xx もあとで送る。それ以外の 4xx は送り直しても通らない
        return put.status === 403 || put.status >= 500 ? { kind: "retry" } : { kind: "failed" };
      }
      await advance({ stage: "uploaded" });
    }

    const serverId = cur.serverId!;
    const { url, body } = ep.submit(serverId, cur);
    try {
      await deps.apiPost(url, body);
    } catch (err) {
      const s = statusOf(err);
      if (s === 400 || s === 404 || s === 409) return { kind: "done", serverId };
      throw err;
    }
    return { kind: "done", serverId };
  } catch (err) {
    await deps.save(cur).catch(() => undefined);
    return isRetryable(err) ? { kind: "retry" } : { kind: "failed" };
  }
}
